import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Clock, Check, X, Loader2 } from 'lucide-react';
import { approveMember, rejectMember, fetchGroupMembers } from '../../../redux/userGroups/groups/userGroupies';

/**
 * Admin-only review surface for outstanding invites/join requests. Pure
 * client-side filter of the already-fetched member list -- no separate
 * backend endpoint needed, approveMember/rejectMember already exist and
 * already target the real backend correctly, they just had no UI dispatching
 * them anywhere until now.
 */
const GroupPendingMembers = ({ pendingMembers, groupId, approveMemberError, rejectMemberError }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [actingUserId, setActingUserId] = useState(null);

  if (!pendingMembers || pendingMembers.length === 0) return null;

  const handleDecision = async (userId, action) => {
    setActingUserId(userId);
    try {
      const thunk = action === 'approve' ? approveMember : rejectMember;
      await dispatch(thunk({ groupId, userId })).unwrap();
      dispatch(fetchGroupMembers(groupId));
    } catch {
      // Was: "errors surface via the shared groups slice error state
      // already" -- false, this component never selected those fields at
      // all. approveMemberError/rejectMemberError are real fields in
      // groupReducer.js (self-clearing on the next attempt via their own
      // .pending case) but had zero renderers anywhere until now.
    } finally {
      setActingUserId(null);
    }
  };

  return (
    <div className="mb-6 bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6">
      <h3 className="text-lg font-semibold text-amber-300 flex items-center space-x-2 mb-4">
        <Clock size={18} />
        <span>{t('groups.pendingMembers.pendingRequestsHeading', { count: pendingMembers.length })}</span>
      </h3>
      {(approveMemberError || rejectMemberError) && (
        <p className="text-sm text-red-500 dark:text-red-400 mb-3">
          {approveMemberError || rejectMemberError}
        </p>
      )}
      <div className="space-y-2">
        {pendingMembers.map((member) => {
          const uid = member.user_id ?? member.id;
          const isActing = actingUserId === uid;
          return (
            <div
              key={uid}
              className="flex items-center justify-between p-3 bg-cream-100 dark:bg-gray-900/40 rounded-lg border border-cream-300 dark:border-gray-700/50"
            >
              <div className="min-w-0">
                <p className="text-gray-900 dark:text-white font-medium truncate">@{member.username || t('groups.pendingMembers.userFallback', { id: uid })}</p>
                {member.joined_at && (
                  <p className="text-gray-500 dark:text-gray-500 text-xs">
                    {t('groups.pendingMembers.requestedOn', { date: new Date(member.joined_at).toLocaleDateString() })}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => handleDecision(uid, 'approve')}
                  disabled={isActing}
                  className="px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white transition-colors"
                >
                  {isActing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  <span>{t('groups.pendingMembers.approve')}</span>
                </button>
                <button
                  onClick={() => handleDecision(uid, 'reject')}
                  disabled={isActing}
                  className="px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-gray-900 dark:text-white transition-colors"
                >
                  <X className="h-4 w-4" />
                  <span>{t('groups.pendingMembers.reject')}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GroupPendingMembers;
